// Export a single image: email clients do not reliably support object-fit or overlays.
export const HEADER_WIDTH = 1200;
export const HEADER_HEIGHT = 543;
export const MAX_HEADER_BYTES = 30 * 1024 * 1024;
export const HEADER_TYPES = ["image/jpeg", "image/png", "image/webp"];

export function cropRectangle(width, height, { x = 50, y = 50, zoom = 1 }) {
  const scale = Math.max(HEADER_WIDTH / width, HEADER_HEIGHT / height) * zoom;
  const cropWidth = HEADER_WIDTH / scale;
  const cropHeight = HEADER_HEIGHT / scale;
  return [(width - cropWidth) * x / 100, (height - cropHeight) * y / 100, cropWidth, cropHeight];
}

export function loadHeaderImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      if (!image.naturalWidth || image.naturalWidth * image.naturalHeight > 100000000) {
        reject(new Error("image-size"));
      } else resolve(image);
    };
    image.onerror = () => reject(new Error("image-load"));
    image.src = url;
  });
}

export function drawHeaderArtwork(canvas, source, options, logo) {
  canvas.width = HEADER_WIDTH;
  canvas.height = HEADER_HEIGHT;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, HEADER_WIDTH, HEADER_HEIGHT);
  ctx.drawImage(source, ...cropRectangle(source.naturalWidth, source.naturalHeight, options), 0, 0, HEADER_WIDTH, HEADER_HEIGHT);
  if (logo) {
    const left = options.cardPosition === "left" ? 60 : 800;
    // Rounded top corners and a flush bottom match the existing email card.
    ctx.fillStyle = "#30373c";
    ctx.beginPath();
    ctx.moveTo(left + 20, 150);
    ctx.lineTo(left + 320, 150);
    ctx.quadraticCurveTo(left + 340, 150, left + 340, 170);
    ctx.lineTo(left + 340, HEADER_HEIGHT);
    ctx.lineTo(left, HEADER_HEIGHT);
    ctx.lineTo(left, 170);
    ctx.quadraticCurveTo(left, 150, left + 20, 150);
    ctx.fill();
    const scale = Math.min(280 / logo.naturalWidth, 280 / logo.naturalHeight);
    const width = logo.naturalWidth * scale;
    const height = logo.naturalHeight * scale;
    ctx.drawImage(logo, left + (340 - width) / 2, 180 + (330 - height) / 2, width, height);
  }
}

export const headerBlob = (canvas, quality = 0.9) => new Promise((resolve, reject) =>
  canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("image-export")), "image/jpeg", quality));

export async function createHeaderArtwork(source, options, logo) {
  const canvas = document.createElement("canvas");
  drawHeaderArtwork(canvas, source, options, logo);
  return headerBlob(canvas);
}

export async function compressHeaderSource(source) {
  const canvas = document.createElement("canvas");
  const scale = Math.min(1, 2400 / Math.max(source.naturalWidth, source.naturalHeight));
  canvas.width = Math.round(source.naturalWidth * scale);
  canvas.height = Math.round(source.naturalHeight * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  for (const quality of [0.9, 0.8, 0.65]) {
    const blob = await headerBlob(canvas, quality);
    if (blob.size <= 5 * 1024 * 1024) return blob;
  }
  throw new Error("image-size");
}

export const clampPosition = value => Math.max(0, Math.min(100, value));
export function dragHeaderCrop(source, crop, dx, dy, displayWidth) {
  const scale = Math.max(HEADER_WIDTH / source.naturalWidth, HEADER_HEIGHT / source.naturalHeight) * crop.zoom;
  const overflowX = source.naturalWidth * scale - HEADER_WIDTH;
  const overflowY = source.naturalHeight * scale - HEADER_HEIGHT;
  const ratio = HEADER_WIDTH / displayWidth;
  return { ...crop,
    x: overflowX > 0.01 ? clampPosition(crop.x - dx * ratio / overflowX * 100) : crop.x,
    y: overflowY > 0.01 ? clampPosition(crop.y - dy * ratio / overflowY * 100) : crop.y,
  };
}
